import React from 'react';
import { useTranslation } from 'react-i18next';
import { VehicleAutoNegoSession, AutoNegoState } from '../../models/vehicleAutoNegoSession';
import { Vehicle } from '../../models/vehicle';
import { useToast } from '../../context/toastContext';

interface NeedsHumanItem {
  vehicleId: number;
  vehiclePlate: string;
  type: 'review' | 'confirm' | 'error';
  title: string;
  description: string;
  timestamp?: Date;
  negoSession?: VehicleAutoNegoSession;
}

interface NeedsHumanQueueProps {
  vehicles: Vehicle[];
  autoNegoSessions: Map<number, VehicleAutoNegoSession | null>;
  onRefresh: () => void;
  onReviewNegotiation: (vehicleId: number) => void;
  loading?: boolean;
  compact?: boolean;
}

const NeedsHumanQueue: React.FC<NeedsHumanQueueProps> = ({
  vehicles,
  autoNegoSessions,
  onReviewNegotiation
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const shownItemsRef = React.useRef<Set<string>>(new Set());

  // Compute items that need human attention
  const needsHumanItems: NeedsHumanItem[] = React.useMemo(() => {
    const items: NeedsHumanItem[] = [];

    vehicles.forEach(vehicle => {
      const negoSession = autoNegoSessions.get(vehicle.id);
      if (!negoSession) return;

      const vehiclePlate = vehicle.truckPlate || `Vehicle ${vehicle.id}`;

      // Negotiations needing review
      if (negoSession.autoNegoState === AutoNegoState.RESULT_TO_REVIEW) {
        items.push({
          vehicleId: vehicle.id,
          vehiclePlate,
          type: 'review',
          title: t('Review Required'),
          description: t('AI found matches that need your approval'),
          timestamp: negoSession.startedAt ? new Date(negoSession.startedAt) : undefined,
          negoSession,
        });
      }

      // Negotiations waiting for results (pending confirmation)
      if (negoSession.autoNegoState === AutoNegoState.WAITING_RESULTS) {
        items.push({
          vehicleId: vehicle.id,
          vehiclePlate,
          type: 'confirm',
          title: t('Pending Confirmation'),
          description: t('Waiting for carrier response'),
          timestamp: negoSession.startedAt ? new Date(negoSession.startedAt) : undefined,
          negoSession,
        });
      }

      // Errored sessions
      if (negoSession.autoNegoState === AutoNegoState.ERRORED) {
        items.push({
          vehicleId: vehicle.id,
          vehiclePlate,
          type: 'error',
          title: t('Action Required'),
          description: negoSession.statusMessage || t('Something went wrong with automation'),
          timestamp: negoSession.startedAt ? new Date(negoSession.startedAt) : undefined,
          negoSession,
        });
      }
    });

    // Sort by priority: errors first, then reviews, then pending
    return items.sort((a, b) => {
      const priority = { error: 0, review: 1, confirm: 2 };
      return priority[a.type] - priority[b.type];
    });
  }, [vehicles, autoNegoSessions, t]);

  // Show toasts with actions for items needing attention
  React.useEffect(() => {
    const currentItemKeys = new Set(needsHumanItems.map(item => `${item.vehicleId}-${item.type}`));
    
    // Show toasts for new items
    needsHumanItems.forEach(item => {
      const key = `${item.vehicleId}-${item.type}`;
      if (!shownItemsRef.current.has(key)) {
        const message = `${item.vehiclePlate}: ${item.title} - ${item.description}`;
        const action = {
          label: item.type === 'review' ? t('Review') : t('View'),
          onClick: () => onReviewNegotiation(item.vehicleId),
        };
        toast.showToast(message, item.type === 'error' ? 'error' : 'warning', 0, action);
        shownItemsRef.current.add(key);
      }
    });
    
    // Remove keys for items that are no longer present
    for (const key of shownItemsRef.current) {
      if (!currentItemKeys.has(key)) {
        shownItemsRef.current.delete(key);
      }
    }
  }, [needsHumanItems, toast, t, onReviewNegotiation]);

  // Only show toasts, no UI rendering
  return null;
};

export default NeedsHumanQueue;
